import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { environment } from '@siat-fe/env';
import {
  EMPTY,
  catchError,
  map,
  mergeMap,
  of,
  pipe,
  share,
  switchMap,
  timer,
} from 'rxjs';
import {
  Widget,
  WidgetApiConfig,
  WidgetData,
  WidgetID,
  WidgetType,
} from '../components';

/**
 * A single raw row/record as returned by a widget's backend endpoint, before
 * being mapped to the widget-type-specific `WidgetData` shape.
 */
export type WidgetApiRow = Record<string, unknown>;

/**
 * The Spring-style paginated envelope most widget endpoints respond with,
 * though some endpoints just return a bare array of rows.
 */
export interface WidgetPaginatedResponse {
  content?: WidgetApiRow[];
  page?: {
    totalElements?: number;
    number?: number;
    size?: number;
  };
  [key: string]: unknown;
}

export type WidgetApiResponse =
  | WidgetPaginatedResponse
  | WidgetApiRow[]
  | WidgetApiRow;

export interface WidgetDataState {
  // Widget data indexed by widget ID
  widgetData: Record<string, WidgetData>;
  // Loading state for each widget
  loadingStates: Record<string, boolean>;
  // Error states for each widget
  errorStates: Record<string, string | null>;
  // Last updated timestamps for each widget
  lastUpdated: Record<string, Date>;
  // Auto-refresh intervals for widgets with refresh intervals
  refreshIntervals: Record<string, number>;
  // Shared requests cache to avoid duplicate API calls for same endpoint
  sharedRequests: Record<string, unknown>;
}

const initialState: WidgetDataState = {
  widgetData: {},
  loadingStates: {},
  errorStates: {},
  lastUpdated: {},
  refreshIntervals: {},
  sharedRequests: {},
};

function transformToCountFormat(
  widget: Widget,
  response: WidgetPaginatedResponse
) {
  return {
    data: widget,
    value: response.page?.totalElements ?? 0,
    label: widget.title,
    unit: 'elementi',
  };
}

function transformToListFormat(
  widget: Widget,
  response: WidgetPaginatedResponse
) {
  const fields = widget.config?.fields || [];
  return {
    items: (response.content || []).map((item: WidgetApiRow) => ({
      id: item['id'] as string,
      label: item[fields[0]],
      value: item[fields[1]],
      icon: item['icon'] as string | undefined,
      path: item['path'] as string | undefined,
    })),
    totalCount: response.page?.totalElements ?? 0,
    hasMore: false,
  };
}

function transformToTableFormat(widget: Widget, response: WidgetApiResponse) {
  const paginatedResponse = response as WidgetPaginatedResponse;
  if (
    paginatedResponse?.content &&
    Array.isArray(paginatedResponse.content)
  ) {
    const rows = paginatedResponse.content;
    const labels = widget.config?.labels || [];

    const columns = labels.map(label => ({
      key: widget.config?.fields?.[labels.indexOf(label)] || label,
      title: label.charAt(0).toUpperCase() + label.slice(1),
      type: 'text' as const,
      sortable: true,
    }));

    return {
      columns,
      rows,
      totalCount: paginatedResponse.page?.totalElements || rows.length,
      currentPage: paginatedResponse.page?.number || 0,
      pageSize: paginatedResponse.page?.size || 10,
    };
  }

  if (!Array.isArray(response) || response.length === 0) {
    return {
      columns: [],
      rows: [],
      totalCount: 0,
      currentPage: 0,
      pageSize: 10,
    };
  }

  const firstRow = response[0];
  const columns = Object.keys(firstRow).map(key => ({
    key,
    title: key.charAt(0).toUpperCase() + key.slice(1),
    type: 'text' as const,
    sortable: true,
  }));

  return {
    columns,
    rows: response,
    totalCount: response.length,
    currentPage: 0,
    pageSize: response.length,
  };
}

function transformToChartFormat(widget: Widget, response: WidgetApiResponse) {
  if (!Array.isArray(response)) {
    return { type: 'bar' as const, data: [] };
  }

  return {
    type: 'bar' as const,
    data: response.map((item: WidgetApiRow) => ({
      label: (item['label'] || item['name'] || 'Item') as string,
      value: (item['value'] || item['count'] || 0) as number,
      color: item['color'] as string | undefined,
    })),
    options: {
      showLegend: true,
      showGrid: true,
    },
  };
}

function extractResponseData(
  widget: Widget,
  response: WidgetApiResponse
): WidgetApiResponse {
  const responseKey = widget.config?.['responseKey'] as string | undefined;
  if (
    responseKey &&
    response &&
    !Array.isArray(response) &&
    response[responseKey]
  ) {
    return response[responseKey] as WidgetApiResponse;
  }
  return response;
}

function transformResponseByWidgetType(
  widget: Widget,
  rawResponse: WidgetApiResponse
): WidgetData {
  const originalResponse = rawResponse;
  const response = extractResponseData(widget, rawResponse);
  let result: WidgetData;
  switch (widget.type) {
    case WidgetType.COUNT:
      result = transformToCountFormat(
        widget,
        response as WidgetPaginatedResponse
      );
      break;
    case WidgetType.LIST:
      result = transformToListFormat(
        widget,
        response as WidgetPaginatedResponse
      );
      break;
    case WidgetType.TABLE:
    case WidgetType.CARD:
      result = transformToTableFormat(widget, response);
      break;
    case WidgetType.CHART:
      result = transformToChartFormat(widget, response);
      break;
    default:
      result = response as WidgetData;
  }

  const responseKey = widget.config?.['responseKey'] as string | undefined;
  if (
    responseKey &&
    originalResponse &&
    typeof originalResponse === 'object' &&
    !Array.isArray(originalResponse)
  ) {
    const { [responseKey]: _, ...metadata } = originalResponse;
    if (Object.keys(metadata).length > 0) {
      (result as Record<string, unknown>)['_metadata'] = metadata;
    }
  }

  return result;
}

function generateRequestKey(apiConfig: WidgetApiConfig): string {
  const { endpoint, method = 'GET', params = {}, body } = apiConfig;
  const sortedParams = Object.keys(params)
    .sort()
    .reduce((sorted: Record<string, string | number | boolean>, key) => {
      sorted[key] = params[key];
      return sorted;
    }, {});

  return JSON.stringify({
    endpoint,
    method,
    params: sortedParams,
    body,
  });
}

function loadWidgetDataInternal(widget: Widget, httpClient: HttpClient) {
  const apiConfig = widget.config?.api;
  if (!apiConfig?.endpoint) {
    return EMPTY;
  }

  // Handle endpoints that already include query parameters
  const [baseEndpoint, queryString] = apiConfig.endpoint.split('?');
  const fullUrl = `${environment.baseUrl}v1/${baseEndpoint}`;

  // Merge existing query params with additional params
  const params: { [key: string]: string } = {};
  const method = apiConfig.method || 'GET';
  // The Fetch API throws "Request with GET/HEAD method cannot have body" if a
  // body is present, even an empty object (which is truthy in JS) — unlike
  // the legacy XHR backend, which silently ignored it.
  const body =
    method === 'GET' || method === 'HEAD' ? null : apiConfig.body || null;

  // Parse existing query parameters from URL
  if (queryString) {
    const urlParams = new URLSearchParams(queryString);
    urlParams.forEach((value, key) => {
      params[key] = value;
    });
  }

  // Add additional params from config
  if (apiConfig.params) {
    Object.entries(apiConfig.params).forEach(([key, value]) => {
      params[key] = String(value);
    });
  }

  const httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'skip-error-handling': 'true',
      'skip-loader': 'true',
    }),
    params,
    body,
  };

  return httpClient
    .request<WidgetApiResponse>(method, fullUrl, httpOptions)
    .pipe(
      catchError(error => {
        console.error(
          `HTTP Error loading data for widget ${widget.id} from ${fullUrl}:`,
          error
        );
        throw error;
      })
    );
}

@Injectable({
  providedIn: 'root',
})
export class WidgetDataStore extends signalStore(
  { providedIn: 'root' },
  withState<WidgetDataState>(initialState),
  withMethods((store, httpClient = inject(HttpClient)) => ({
    /**
     * Initialize widget data loading
     */
    initializeWidget: rxMethod<Widget>(
      pipe(
        mergeMap(widget => {
          if (!widget.config?.api?.endpoint) {
            return of(null);
          }
          // Set initial loading state immediately
          patchState(store, state => ({
            loadingStates: { ...state.loadingStates, [widget.id]: true },
            errorStates: { ...state.errorStates, [widget.id]: null },
          }));

          // Generate request key for sharing requests
          const requestKey = generateRequestKey(widget.config.api);

          // Check if we already have a shared request for this endpoint
          const existingRequest = store.sharedRequests()[requestKey];

          let dataRequest$;
          if (existingRequest) {
            dataRequest$ = existingRequest;
          } else {
            dataRequest$ = loadWidgetDataInternal(widget, httpClient).pipe(
              share() // Share the request among multiple subscribers
            );

            if (
              widget.id === WidgetID.DOCUMENTI_DSE ||
              widget.id === WidgetID.PRESCRIZIONI
            ) {
              if (
                !widget.config.api?.body?.['codiceFiscale'] &&
                !widget.config.api?.body?.['idAssistito']
              ) {
                patchState(store, state => ({
                  loadingStates: { ...state.loadingStates, [widget.id]: false },
                }));
                return of([]);
              }
            }

            // Store the shared request
            patchState(store, state => ({
              sharedRequests: {
                ...state.sharedRequests,
                [requestKey]: dataRequest$,
              },
            }));
          }
          return dataRequest$.pipe(
            map(response => ({ widget, response })),
            tapResponse({
              next: ({ widget, response }) => {
                const transformedData = transformResponseByWidgetType(
                  widget,
                  response
                );

                patchState(store, state => ({
                  widgetData: {
                    ...state.widgetData,
                    [widget.id]: transformedData,
                  },
                  loadingStates: { ...state.loadingStates, [widget.id]: false },
                  errorStates: { ...state.errorStates, [widget.id]: null },
                  lastUpdated: {
                    ...state.lastUpdated,
                    [widget.id]: new Date(),
                  },
                }));

                if (
                  widget.config?.api?.refreshInterval &&
                  widget.config.api.refreshInterval > 0
                ) {
                  const existingInterval = store.refreshIntervals()[widget.id];
                  if (existingInterval) {
                    clearInterval(existingInterval);
                    patchState(store, state => {
                      const { [widget.id]: _removed, ...rest } =
                        state.refreshIntervals;
                      return { refreshIntervals: rest };
                    });
                  }
                  const intervalId = setInterval(() => {
                    patchState(store, state => {
                      const { [requestKey]: _removed, ...rest } =
                        state.sharedRequests;
                      return { sharedRequests: rest };
                    });
                    loadWidgetDataInternal(widget, httpClient)
                      .pipe(
                        tapResponse({
                          next: refreshResponse => {
                            const refreshedData = transformResponseByWidgetType(
                              widget,
                              refreshResponse
                            );
                            patchState(store, state => ({
                              widgetData: {
                                ...state.widgetData,
                                [widget.id]: refreshedData,
                              },
                              lastUpdated: {
                                ...state.lastUpdated,
                                [widget.id]: new Date(),
                              },
                            }));
                          },
                          error: () => {},
                        })
                      )
                      .subscribe();
                  }, widget.config.api.refreshInterval);
                  patchState(store, state => ({
                    refreshIntervals: {
                      ...state.refreshIntervals,
                      [widget.id]: intervalId,
                    },
                  }));
                }
              },
              error: (error: unknown) => {
                console.error(
                  `Error loading data for widget ${widget.id}:`,
                  error
                );
                patchState(store, state => ({
                  loadingStates: { ...state.loadingStates, [widget.id]: false },
                  errorStates: {
                    ...state.errorStates,
                    [widget.id]: 'Errore nel caricamento dei dati',
                  },
                  // Clear failed request from cache
                  sharedRequests: {
                    ...state.sharedRequests,
                    [requestKey]: undefined,
                  },
                }));
              },
              finalize: () => {
                // Clean up completed request from cache after a delay
                timer(5000).subscribe(() => {
                  patchState(store, state => {
                    const { [requestKey]: _removed, ...rest } =
                      state.sharedRequests;
                    return { sharedRequests: rest };
                  });
                });
              },
            })
          );
        })
      )
    ),

    /**
     * Refresh data for a specific widget
     */
    /*    refreshWidgetData: rxMethod<Widget>(
      pipe(
        switchMap((widget) => {
          patchState(store, (state) => ({
            loadingStates: { ...state.loadingStates, [widget.id]: true },
            errorStates: { ...state.errorStates, [widget.id]: null }
          }));

          return loadWidgetDataInternal(widget, httpClient).pipe(
            tapResponse({
              next: (transformedData) => {
                patchState(store, (state) => ({
                  widgetData: { ...state.widgetData, [widget.id]: transformedData },
                  loadingStates: { ...state.loadingStates, [widget.id]: false },
                  errorStates: { ...state.errorStates, [widget.id]: null },
                  lastUpdated: { ...state.lastUpdated, [widget.id]: new Date() }
                }));
              },
              error: (error: unknown) => {
                console.error(`Error refreshing data for widget ${widget.id}:`, error);
                patchState(store, (state) => ({
                  loadingStates: { ...state.loadingStates, [widget.id]: false },
                  errorStates: { ...state.errorStates, [widget.id]: 'Errore nel caricamento dei dati' }
                }));
              }
            })
          );
        })
      )
    ),*/

    /**
     * Clear auto-refresh for a widget
     */
    clearAutoRefresh: (widgetId: string) => {
      const intervalId = store.refreshIntervals()[widgetId];
      if (intervalId) {
        clearInterval(intervalId);
        patchState(store, state => {
          const { [widgetId]: _removed, ...rest } = state.refreshIntervals;
          return { refreshIntervals: rest };
        });
      }
    },

    /**
     * Clear widget data when widget is removed
     */
    removeWidget: (widgetId: string) => {
      // Clear auto-refresh first
      const intervalId = store.refreshIntervals()[widgetId];
      if (intervalId) {
        clearInterval(intervalId);
      }

      patchState(store, state => {
        const { [widgetId]: _removedData, ...restData } = state.widgetData;

        const { [widgetId]: _removedLoading, ...restLoading } =
          state.loadingStates;

        const { [widgetId]: _removedError, ...restError } = state.errorStates;

        const { [widgetId]: _removedTimestamp, ...restTimestamps } =
          state.lastUpdated;

        const { [widgetId]: _removedInterval, ...restIntervals } =
          state.refreshIntervals;

        return {
          widgetData: restData,
          loadingStates: restLoading,
          errorStates: restError,
          lastUpdated: restTimestamps,
          refreshIntervals: restIntervals,
        };
      });
    },

    /**
     * Get widget data by ID
     * @returns {T | undefined} The widget data narrowed to the caller's expected shape
     */
    getWidgetData: <T extends WidgetData = WidgetData>(
      widgetId: string
    ): T | undefined => store.widgetData()[widgetId] as T | undefined,

    /**
     * Get widget loading state by ID
     */
    isWidgetLoading: (widgetId: string) =>
      store.loadingStates()[widgetId] || false,

    /**
     * Get widget error state by ID
     */
    getWidgetError: (widgetId: string) => store.errorStates()[widgetId],

    /**
     * Get widget last updated timestamp by ID
     */
    getWidgetLastUpdated: (widgetId: string) => store.lastUpdated()[widgetId],

    /**
     * Delete an item and refresh widget data
     */
    deleteItem: rxMethod<{ widget: Widget; itemId: string }>(
      pipe(
        switchMap(({ widget, itemId }) => {
          const deleteUrl = `${environment.baseUrl}v1/${widget.config.path.delete}/${itemId}`;

          return httpClient.delete(deleteUrl).pipe(
            tapResponse({
              next: () => {
                // Refresh widget data after successful deletion
                loadWidgetDataInternal(widget, httpClient)
                  .pipe(
                    tapResponse({
                      next: response => {
                        const transformedData = transformResponseByWidgetType(
                          widget,
                          response
                        );
                        patchState(store, state => ({
                          widgetData: {
                            ...state.widgetData,
                            [widget.id]: transformedData,
                          },
                          lastUpdated: {
                            ...state.lastUpdated,
                            [widget.id]: new Date(),
                          },
                        }));
                      },
                      error: () => {},
                    })
                  )
                  .subscribe();
              },
              error: (error: unknown) => {
                console.error(`Error deleting item ${itemId}:`, error);
              },
            })
          );
        })
      )
    ),
  }))
) {}
